import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ComponentProps } from "react";

// 무빈소 절차 타임라인(장례/절차 퍼널 공용). 자택 → 입관식 → 발인.

export interface MubinsoTimelineItem {
  /** 상단 소제목(예: "장례 첫날", "다음 날 · 발인 전") */
  phase: string;
  /** 본문 제목(예: "자택에서 머무르세요") */
  title: string;
  /** 보조 설명(옵션) */
  desc?: string;
  /** 강조(오렌지 도트 + 라벨) */
  highlight?: boolean;
}

export function MubinsoTimeline({ items }: { items: MubinsoTimelineItem[] }) {
  return (
    <BaseStack width="100%">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <BaseStack key={item.title} direction="horizontal" spacing={14} alignItems="stretch">
            {/* 좌측: 도트 + 연결선 */}
            <BaseStack flex="none" alignItems="center" spacing={0}>
              <Box
                flex="none"
                width="10px"
                height="10px"
                mt="5px"
                borderRadius="50%"
                bg={item.highlight ? colors.orange650 : colors.gray300}
              />
              {!isLast && <Box flex={1} width="1px" bg={colors.gray200} />}
            </BaseStack>
            {/* 우측: 콘텐츠 */}
            <Box pb={isLast ? "0" : "24px"} minWidth={0}>
              <Box
                className="caption_12_b"
                color={item.highlight ? colors.orange650 : colors.gray500}
              >
                {item.phase}
              </Box>
              <Box className="body_16_m gray900" pt="4px">
                {item.title}
              </Box>
              {item.desc && (
                <Box className="body_14_r gray500" pt="4px" whiteSpace="pre-line">
                  {item.desc}
                </Box>
              )}
            </Box>
          </BaseStack>
        );
      })}
    </BaseStack>
  );
}

/**
 * 아이콘 노드형 타임라인(E4 절차 퍼널). 단계마다 디자인 시스템 그래픽 아이콘(원래 색 유지) 원을
 * 세로선으로 잇는다.
 * highlight 단계(가족이 직접 참여하는 입관식)는 노드 배경과 단계 라벨만 주황으로 바꾼다. 박스로 감싸지 않는다.
 * 현행 퍼널은 MubinsoTimeline 을 그대로 쓴다.
 */
export function MubinsoIconTimeline({
  items,
}: {
  items: (MubinsoTimelineItem & { icon: ComponentProps<typeof ColorIcon>["name"] })[];
}) {
  return (
    <BaseStack width="100%">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <BaseStack key={item.title} direction="horizontal" spacing={14} alignItems="stretch">
            <BaseStack flex="none" width="44px" alignItems="center" spacing={0}>
              <Box
                flex="none"
                width="44px"
                height="44px"
                borderRadius="50%"
                bg={item.highlight ? colors.orange200 : colors.warmGray100}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ColorIcon name={item.icon} size={26} />
              </Box>
              {!isLast && <Box flex={1} width="2px" my="4px" bg={colors.gray200} />}
            </BaseStack>
            <BaseStack flex={1} minWidth={0} pt="2px" pb={isLast ? "0" : "24px"}>
              <Box
                className="caption_12_b"
                color={item.highlight ? colors.orange650 : colors.gray500}
              >
                {item.phase}
              </Box>
              {/* 라벨과 제목은 한 묶음이라 2px(시각 보정), 제목과 설명은 4px */}
              <Spacing gap={2} />
              <Box className="subtitle_16_b gray900">{item.title}</Box>
              {item.desc && (
                <>
                  <Spacing gap={4} />
                  <Box
                    className="body_14_r gray600"
                    whiteSpace="pre-line"
                    style={{ wordBreak: "keep-all" }}
                  >
                    {item.desc}
                  </Box>
                </>
              )}
            </BaseStack>
          </BaseStack>
        );
      })}
    </BaseStack>
  );
}
